import {
  Badge,
  SearchableSelect,
  searchableSource,
  type FormConfig,
  type FormFieldConfig,
  type OptionQuery,
  type UseFormResult,
} from '@eo/shared';
import type { ReactNode } from 'react';

import type { ClientItem, StaffItem } from '../api/types';
import { UNASSIGNED_OWNER } from '../constants';
import {
  type ClientFormValues,
  type NewStaffValues,
  type OptionPickers,
  type StaffFormValues,
} from '../types';

const query =
  (onQueryOptions: OptionPickers['onQueryOptions'], source: OptionQuery['source']) =>
  (params: { search: string; page: number }) =>
    onQueryOptions({ source, ...params });

// FormConfig's built-in types are text/number/textarea/select, so booleans and
// dates come through the `render` escape hatch.
function checkbox<T>(id: Extract<keyof T, string>, label: string) {
  return (form: UseFormResult<T>): ReactNode => (
    <label className="flex items-center gap-2 text-sm text-foreground">
      <input
        type="checkbox"
        checked={Boolean(form.values[id])}
        onChange={(e) => form.set(id, e.target.checked as T[typeof id])}
        className="h-4 w-4 accent-primary"
      />
      {label}
    </label>
  );
}

// A client has exactly one owner, so the picker labels one that is already
// owned with whose it is: adding it here moves it, and that is never silent.
function clientsField(
  clients: ClientItem[],
  staff: StaffItem[],
  { options, onQueryOptions }: OptionPickers,
): FormFieldConfig<StaffFormValues, void> {
  const nameById = new Map(staff.map((member) => [member.id, member.name]));
  const known = [...options.clients.items, ...clients];
  const nameOf = (id: string) => known.find((client) => client.id === id)?.name ?? id;
  return {
    id: 'clientIds',
    label: 'Clients',
    render: (form) => {
      const selected = form.values.clientIds;
      return (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-1">
            {selected.length === 0 ? (
              <span className="text-xs text-muted-foreground">No clients assigned.</span>
            ) : (
              selected.map((id) => (
                <Badge key={id} variant="secondary" className="gap-1">
                  {nameOf(id)}
                  <button
                    type="button"
                    aria-label={`Unassign ${nameOf(id)}`}
                    onClick={() =>
                      form.set(
                        'clientIds',
                        selected.filter((value) => value !== id),
                      )
                    }
                    className="text-muted-foreground hover:text-foreground"
                  >
                    ×
                  </button>
                </Badge>
              ))
            )}
          </div>
          <SearchableSelect
            value=""
            ariaLabel="Client to assign"
            placeholder="Add a client"
            emptyLabel="No client matches"
            options={options.clients.items.map((client) => {
              const current = client.ownerUserId ? nameById.get(client.ownerUserId) : null;
              return {
                value: client.id,
                label: current ? `${client.name} — ${current}` : client.name,
                disabled: selected.includes(client.id),
              };
            })}
            {...searchableSource(options.clients, query(onQueryOptions, 'clients'))}
            onChange={(value) => form.set('clientIds', [...selected, value])}
          />
        </div>
      );
    },
  };
}

export type StaffEditFormArgs = OptionPickers & { clients: ClientItem[]; staff: StaffItem[] };

export function makeStaffEditFormConfig({
  clients,
  staff,
  ...pickers
}: StaffEditFormArgs): FormConfig<StaffFormValues> {
  return {
    sections: [
      {
        title: 'Profile',
        fields: [
          { id: 'name', label: 'Display name', required: true },
          {
            id: 'role',
            label: 'Role',
            type: 'select',
            colSpan: 1,
            options: [
              { value: 'cfo', label: 'CFO' },
              { value: 'data_team', label: 'Data team' },
            ],
          },
          { id: 'country', label: 'Country', colSpan: 1, placeholder: 'US' },
        ],
        description: 'Country drives the holiday deductions in capacity reporting.',
      },
      {
        title: 'Access',
        fields: [
          {
            id: 'isAdmin',
            label: 'Admin',
            inline: true,
            render: checkbox<StaffFormValues>('isAdmin', 'Staff admin'),
          },
          {
            id: 'isActive',
            label: 'Active',
            inline: true,
            render: checkbox<StaffFormValues>('isActive', 'Active'),
          },
        ],
        description: 'Unticking Active takes effect on the member’s next request.',
      },
      {
        title: 'Clients',
        fields: [clientsField(clients, staff, pickers)],
        description: 'A client has one owner, so adding one that belongs to someone else moves it.',
      },
    ],
  };
}

export const staffCreateFormConfig: FormConfig<NewStaffValues> = {
  fields: [
    { id: 'email', label: 'Email', required: true, placeholder: 'new.person@everandother.com' },
    { id: 'name', label: 'Display name', required: true },
  ],
};

export type ClientFormArgs = OptionPickers & {
  staff: StaffItem[];
};

export function makeClientFormConfig({
  staff,
  options,
  onQueryOptions,
}: ClientFormArgs): FormConfig<ClientFormValues> {
  const known = [...options.users.items, ...staff];
  return {
    fields: [
      { id: 'name', label: 'Client name', required: true },
      {
        id: 'aliases',
        label: 'Also known as',
        placeholder: 'MM, Magic M',
        description: 'Short names meeting titles use for this client, separated by commas.',
      },
      {
        id: 'domains',
        label: 'Email domains',
        placeholder: 'magicmolecule.com',
        description: 'Domains its people write from, separated by commas.',
      },
      {
        id: 'slug',
        label: 'Slug',
        placeholder: 'highld',
        description:
          'The short name the E+O platform files this client under, which its excess hours reports go out with. Left blank, it comes from the name.',
      },
      {
        id: 'ownerUserId',
        label: 'Owner',
        render: (form) => (
          <SearchableSelect
            value={form.values.ownerUserId}
            selectedLabel={known.find((m) => m.id === form.values.ownerUserId)?.name}
            ariaLabel="Owner"
            placeholder="Unassigned"
            options={[
              { value: UNASSIGNED_OWNER, label: 'Unassigned', pinned: true },
              ...options.users.items.map((member) => ({ value: member.id, label: member.name })),
            ]}
            {...searchableSource(options.users, query(onQueryOptions, 'users'))}
            onChange={(value) => form.set('ownerUserId', value)}
          />
        ),
      },
      {
        id: 'isActive',
        label: 'Active',
        inline: true,
        render: checkbox<ClientFormValues>('isActive', 'Active'),
      },
    ],
  };
}
